/**
 * @fileoverview Queue Section component.
 * @summary Displays and manages the song queue with drag-and-drop functionality.
 *
 * @package KaraoNow
 * @module apps/front/src/pages/organizer/components/QueueSection
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faMusic,
  faExclamationTriangle,
  faTimes,
  faArrowRotateRight,
} from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SortableQueueItemRow } from './SortableQueueItemRow'
import Spinner from '../../../components/Spinner'
import type { QueueItem, QueueItemStatus } from '../../../store/slices/queueSlice'

interface QueueSectionProps {
  isOpen: boolean
  isLoading: boolean
  error: string | null
  actionError: string | null
  items: QueueItem[]
  sensors: any
  onRefresh: () => void
  onDragEnd: (event: DragEndEvent) => void
  onStatusChange: (item: QueueItem, status: QueueItemStatus) => void
  onDelete: (itemId: string) => void
  onActionErrorDismiss: () => void
}

/**
 * @summary Queue section component for displaying and managing the song queue.
 * @param {QueueSectionProps} props - Component props.
 * @returns {React.ReactElement} Queue section or null if not open.
 */
export function QueueSection({
  isOpen,
  isLoading,
  error,
  actionError,
  items,
  sensors,
  onRefresh,
  onDragEnd,
  onStatusChange,
  onDelete,
  onActionErrorDismiss,
}: QueueSectionProps) {
  const { t } = useTranslation()

  if (!isOpen) {
    return null
  }

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-(--color-text-primary) flex items-center gap-2">
          <FontAwesomeIcon icon={faMusic} />
          {t('events.queue')}
        </h2>
        <button
          onClick={onRefresh}
          className="btn btn-secondary btn-sm"
          disabled={isLoading}
          title={t('common.refresh')}
        >
          <FontAwesomeIcon 
            icon={faArrowRotateRight} 
            className={isLoading ? 'animate-spin' : ''}
          />
        </button>
      </div>

      {error && (
        <div className="alert alert-error mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{error}</span>
        </div>
      )}

      {actionError && (
        <div className="alert alert-error mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>{actionError}</span>
          <button
            onClick={onActionErrorDismiss}
            className="ml-auto text-(--color-text-secondary) hover:text-(--color-text-primary)"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}

      {/* Queue List with optional overlay during loading */}
      <div className="relative">
        {/* Loading overlay - light and positioned on the list */}
        {isLoading && (
          <div className="fixed h-full w-full inset-0 bg-black/10 dark:bg-black/20 z-10 pointer-events-none" />
        )}

        {/* Spinner centered on screen */}
        {isLoading && (
          <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
            <Spinner size="lg" />
          </div>
        )}

        {/* Queue content */}
        {items.length === 0 && !isLoading ? (
          <p className="text-(--color-text-secondary)">{t('events.noQueue')}</p>
        ) : (
          <div className={isLoading ? 'opacity-50 pointer-events-none' : ''}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                items={items.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {items.map((item, index) => (
                    <SortableQueueItemRow
                      key={item.id}
                      item={item}
                      index={index}
                      onStatusChange={(status) => onStatusChange(item, status)}
                      onDelete={() => onDelete(item.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}
      </div>
    </div>
  )
}
